/* eslint-disable no-magic-numbers */

import { useReactiveVar } from '@apollo/client/react';
import { Typography } from '@mui/material';
import Drawer from 'components/Drawer/Drawer';
import { getRoleOptionProp } from 'components/Drawer/variants/externalMembers/shared';
import Modal from 'components/Modal/Modal';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { Maybe, MobileDevice, VisitorGroup } from 'graphql/generated/graphqlTypes';
import useAuthWithCognito from 'hooks/useAuthWithCognito/useAuth';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useExternalUsers from 'hooks/useExternalUsers/useExternalUsers';
import useSortingExternalUsersTable from 'hooks/useSorting/useSortingExternalUsersTable/useSortingExternalUsersTable';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { kebabCase } from 'lodash';
import React, { useEffect, useState } from 'react';
import { authVar } from 'state/vars';
import styled from 'styled-components';
import { Colors } from 'theme/theme';
import Button from 'ui/atoms/Button/Button';
import TableCell from 'ui/atoms/TableCell/TableCell';
import TableHeader from 'ui/atoms/TableHeader/TableHeader';
import NoExternalUserGroups from 'ui/molecules/NoExternalUserGroups/NoExternalUserGroups';
import NoExternalUsersWithButton from 'ui/molecules/NoExternalUsersWithButton/NoExternalUsersWithButton';
import TableHeaderRow from 'ui/molecules/TableHeaderRow/TableHeaderRow';
import TableRow from 'ui/molecules/TableRow/TableRow';
import SearchExternalUsers from 'ui/organisms/SearchExternalUsers/SearchExternalUsers';
import Table from 'ui/organisms/Table/Table';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import PageContent from 'ui/templates/PageContent/PageContent';
import { StickyToTop } from 'ui/templates/StickyToTop/StickyToTop';
import ArrayUtil from 'utils/Array/Array.util';
import { filterBySearchTerm } from 'utils/FilterBySearchTerm/FilterBySearchTerm.util';
import { createUsersTabs } from '../usersTabsConst';

dayjs.extend(isBetween);

export const createHeaderGroups = (translation: Translation, isEntraLess: boolean) => [
  isEntraLess ? translation.user_groups : translation.external_groups
];

export const createColumnsUsers = (translation: Translation, isEntraLess: boolean) => [
  { text: isEntraLess ? translation.users : translation.external_users, width: 40 },
  { text: translation.role, width: 30 },
  { text: translation.expiration_date, width: 30 }
];

const SearchWrapper = styled.div`
  width: 14.875rem;
`;

const ExternalUsers: React.FC = () => {
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const isEntraLess = user?.isEntraLess!;
  const usersTabs = createUsersTabs(translation, isEntraLess);
  const headerGroups = createHeaderGroups(translation, isEntraLess);
  const columnsUsers = createColumnsUsers(translation, isEntraLess);
  const { showDrawer } = useDrawer();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const {
    externalUsersGroups,
    loadingWithRole: externalUsersGroupsLoading,
    addVisitorMemberMutationWithRoleLoading,
    updateVisitorMemberByIdWithRoleLoading,
    deleteVisitorGroupByIdMutationLoading
  } = useExternalUsers({
    translation,
    handleFetchError
  });
  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const skeletonArray = ArrayUtil.SkeletonArray();
  const { handleSortingGroupsIcon, handleSortingGroupsOnClick, handleSortingUsersIcon, handleSortingUsersOnClick } =
    useSortingExternalUsersTable(translation);
  const [filterStr, setFilterStr] = useState('');
  const [filterGroupId, setFilterGroupId] = useState<string | undefined>();
  const [filterMemberId, setFilterMemberId] = useState<string | undefined>();
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false);
  const { isMobileDeviceRegisteredWithAccess } = useAuthWithCognito();
  const getGroupById = (groupId: string) => externalUsersGroups.find((group) => group.id === groupId);

  const getGroupByMemberId = (memberId: string) =>
    externalUsersGroups.find((group) => group.members.some((member) => member.id === memberId));

  const getMemberById = (memberId: string) => {
    const groupWithMember = externalUsersGroups.find((group) => group.members.some((member) => member.id === memberId));
    return groupWithMember?.members.find((member) => member.id === memberId);
  };

  const getFilteredGroups = () => {
    if (filterGroupId !== undefined) {
      const filteredGroup = getGroupById(filterGroupId);
      return filteredGroup ? [filteredGroup as VisitorGroup] : undefined;
    }
    if (filterMemberId !== undefined) {
      const filteredGroup = getGroupByMemberId(filterMemberId);
      return filteredGroup ? [filteredGroup as VisitorGroup] : undefined;
    }
    return externalUsersGroups as VisitorGroup[];
  };

  const getFirstSectionItems = () =>
    filterBySearchTerm(externalUsersGroups, filterStr, (group) => [group.name]).map((group) => ({
      id: group.id,
      text: group.name
    }));

  const getSecondSectionItems = () =>
    externalUsersGroups.flatMap((group) =>
      filterBySearchTerm(group.members, filterStr, (element) => [element.name, element.email]).flatMap((member) => ({
        id: member.id,
        text: member.name,
        email: member.email,
        additionalInfoText: group.name
      }))
    );

  const handleFirstSectionRowOnClick = (id: string) => {
    setIsSearchMenuOpen(false);
    setFilterGroupId(id);
    setFilterMemberId(undefined);
    setSelectedGroupId(id);
    const filteredGroups = getGroupById(id);
    if (filteredGroups) setFilterStr(filteredGroups.name);
  };

  const handleSecondSectionRowOnClick = (id: string) => {
    setIsSearchMenuOpen(false);
    setFilterGroupId(undefined);
    setFilterMemberId(id);

    const filteredGroups = getGroupByMemberId(id);
    if (filteredGroups) setSelectedGroupId(filteredGroups.id);
    const filteredMember = getMemberById(id);
    if (filteredMember) setFilterStr(filteredMember.name);
  };

  const handleClearButtonOnClick = () => {
    setIsSearchMenuOpen(false);
    setFilterGroupId(undefined);
    setFilterMemberId(undefined);
    setFilterStr('');
  };

  const handleSetFilterStrOnChange = (text: string) => {
    if (text === '') handleClearButtonOnClick();
    setIsSearchMenuOpen(Boolean(text));
    setFilterStr(text);
  };

  useEffect(() => {
    if (selectedGroupId === undefined && externalUsersGroups.length > 0 && externalUsersGroups[0].id)
      setSelectedGroupId(externalUsersGroups[0].id);
  }, [externalUsersGroups, externalUsersGroupsLoading, selectedGroupId]);

  const handleAddExternalUserGroupClick = () => {
    showDrawer({
      type: 'addExternalUserGroup'
    });
  };

  const handleAddExternalUserClick = (visitorGroup: VisitorGroup) => {
    if (visitorGroup)
      showDrawer({
        type: 'addExternalUser',
        contentValue: { visitorGroupId: visitorGroup.id }
      });
  };

  const isAdminEditingHimself = (userId: string | undefined, visitorId: string) => Boolean(userId === visitorId);

  const handleEditExternalUserClick = (
    name: string,
    email: string | null,
    mobileDevices: MobileDevice[],
    id: string,
    group: string,
    isEditable: boolean,
    expirationDate: string | null,
    role: string | null,
    isAdmin: boolean
  ) => {
    if (
      !externalUsersGroupsLoading &&
      !addVisitorMemberMutationWithRoleLoading &&
      !updateVisitorMemberByIdWithRoleLoading
    )
      showDrawer({
        type: 'editExternalUser',
        contentValue: {
          name,
          email,
          isUserAddedViaKeySharing: getIsUserAddedViaKeySharing(email),
          mobileDevices,
          id,
          group,
          isEditable,
          expirationDate,
          role,
          isAdmin,
          isAdminEditingHimself: isAdminEditingHimself(user?.id, id)
        }
      });
  };

  const handleEditExternalUserGroupClick = (
    name: string,
    visitorGroupId: string,
    maxNumberOfMembers: number | null
  ) => {
    showDrawer({
      type: 'editExternalUserGroup',
      contentValue: {
        name,
        visitorGroupId,
        maxNumberOfMembers
      }
    });
  };

  const getSelectedGroup = (): VisitorGroup | undefined => {
    if (filterMemberId !== undefined) {
      const groupsWithFilterMember = getGroupByMemberId(filterMemberId);
      if (groupsWithFilterMember && selectedGroupId) {
        const selectedGroup = getGroupById(selectedGroupId) as VisitorGroup;
        if (selectedGroup) {
          return { ...selectedGroup, members: selectedGroup.members.filter((member) => member.id === filterMemberId) };
        }
      }
    }
    return selectedGroupId ? (getGroupById(selectedGroupId) as VisitorGroup) : undefined;
  };

  const isSelectedGroupHaveMembers = () => {
    const selectedGroup = getSelectedGroup();
    return selectedGroup ? selectedGroup.members.length > 0 : undefined;
  };

  const isSelectedGroupNotHaveMembers = () => {
    const selectedGroup = getSelectedGroup();
    return selectedGroup ? selectedGroup.members.length === 0 : undefined;
  };

  const isTableVisible = () =>
    externalUsersGroupsLoading || (!externalUsersGroupsLoading && externalUsersGroups.length > 0);
  const isTableLoadingVisible = () => externalUsersGroupsLoading;
  const isTableGroupsRowsWithDataVisible = () => !externalUsersGroupsLoading && externalUsersGroups.length > 0;
  const isTableMembersRowsWithDataVisible = () =>
    !externalUsersGroupsLoading &&
    !deleteVisitorGroupByIdMutationLoading &&
    externalUsersGroups.length > 0 &&
    isSelectedGroupHaveMembers();
  const isTableNoGroupsVisible = () =>
    !externalUsersGroupsLoading && !deleteVisitorGroupByIdMutationLoading && externalUsersGroups.length === 0;
  const isTableMembersNoMembersVisible = () =>
    !externalUsersGroupsLoading &&
    !deleteVisitorGroupByIdMutationLoading &&
    externalUsersGroups.length > 0 &&
    isSelectedGroupNotHaveMembers();

  useEffect(() => {
    if (deleteVisitorGroupByIdMutationLoading) setSelectedGroupId(undefined);
  }, [deleteVisitorGroupByIdMutationLoading]);

  const handleColorDate = (expirationDateTo: string | null) => {
    if (expirationDateTo) {
      if (dayjs(expirationDateTo).isBefore(dayjs().add(24, 'h'), 'h')) return 'error';
      if (dayjs(expirationDateTo).isBetween(dayjs(), dayjs().add(1, 'week'), 'day')) return 'warning';
    }
    return 'lTextHigh';
  };

  const isGroupSortingAvailable = filterGroupId === undefined && filterMemberId === undefined;
  const isMemberSortingAvailable = () => {
    if (filterGroupId) {
      const filteredGroup = getGroupById(filterGroupId);
      return Boolean(filteredGroup && filteredGroup.members.length > 1);
    }
    if (filterMemberId) return false;
    return true;
  };

  const getMemberRoleInSelectedGroup = (memberId: string) => {
    const selectedGroup = getSelectedGroup();

    return selectedGroup?.members
      .find((member) => member.id === memberId)
      ?.visitorGroupMembership.find((groupMembership) => groupMembership.visitorGroup.id === selectedGroup.id)?.role;
  };

  const getMemberRoleCellText = (memberId: string) => {
    const memberRoleInSelectedGroup = getMemberRoleInSelectedGroup(memberId);
    if (memberRoleInSelectedGroup) {
      const roleOptionProp = getRoleOptionProp(memberRoleInSelectedGroup, translation);
      if (roleOptionProp) return roleOptionProp.label;
    }
    return '-';
  };

  const getBeforeContentText = (
    currentNumberOfMembers: number,
    maximumNumberOfMembers: number | null
  ): string | undefined => {
    if (maximumNumberOfMembers) return `${currentNumberOfMembers}/${maximumNumberOfMembers}`;
    return undefined;
  };

  const getBeforeContentTextColor = (
    currentNumberOfMembers: number,
    maximumNumberOfMembers: number | null
  ): keyof Colors | undefined => {
    if (maximumNumberOfMembers && currentNumberOfMembers === maximumNumberOfMembers) return `error`;
    return undefined;
  };

  const isButtonAddMemberDisabled = () => {
    const selectedGroup = getSelectedGroup();
    return (
      (!isTableVisible() && !selectedGroup) ||
      (selectedGroup !== undefined && selectedGroup.members.length === selectedGroup.maxNumberOfMembers)
    );
  };

  const getIsUserAddedViaKeySharing = (email: string | null) => email === null;

  const getSecondLineText = (email: Maybe<string> | undefined) => {
    if (email !== undefined && getIsUserAddedViaKeySharing(email)) return translation.invited_via_key_sharing;
    return email;
  };

  return (
    <PageContent title="ATLAS" titleId="location" tabs={usersTabs} id="external-users-page">
      <StickyToTop>
        <ComponentWrapper alignItems="center" justifyContent="space-between" gap="0.5rem" width="100%">
          <SearchWrapper>
            <SearchExternalUsers
              isOpen={isSearchMenuOpen}
              filterStr={filterStr}
              setFilterStr={handleSetFilterStrOnChange}
              firstSectionItems={getFirstSectionItems()}
              secondSectionItems={getSecondSectionItems()}
              firstHeader={translation.external_groups}
              secondHeader={translation.external_users}
              handleFirstSectionRowOnClick={handleFirstSectionRowOnClick}
              handleSecondSectionRowOnClick={handleSecondSectionRowOnClick}
              handleClearButtonOnClick={handleClearButtonOnClick}
            />
          </SearchWrapper>
          <ComponentWrapper width="24rem" gap="24px">
            <Button
              onClick={handleAddExternalUserGroupClick}
              id="add-external-group-button"
              backgroundColor="transparent"
              style={{ border: '1px solid rgba(204, 204, 0, 1)' }}
            >
              {translation.add_group}
            </Button>
            <Button
              onClick={() => handleAddExternalUserClick(getSelectedGroup()!)}
              id="add-external-group-member-button"
              disabled={isButtonAddMemberDisabled()}
              textColor={isButtonAddMemberDisabled() ? 'dTextLow' : 'lTextHigh'}
              backgroundColor={isButtonAddMemberDisabled() ? 'b3' : 'primary'}
            >
              {translation.add_user}
            </Button>
          </ComponentWrapper>
        </ComponentWrapper>
      </StickyToTop>
      <ComponentWrapper height="calc(100vh - 23rem)" flex="0" justifyContent="center" alignItems="center">
        {isTableVisible() && (
          <>
            <ComponentWrapper height="calc(100vh - 23rem)" width="10rem" flex="0 0 30%">
              <Table
                header={
                  <TableHeaderRow>
                    <TableHeader
                      headerText={headerGroups[0]}
                      id={kebabCase(`header-${headerGroups[0]}`)}
                      flex="0 0 100%"
                      iconSorting={isGroupSortingAvailable ? handleSortingGroupsIcon(headerGroups[0]) : undefined}
                      onClick={isGroupSortingAvailable ? () => handleSortingGroupsOnClick(headerGroups[0]) : undefined}
                    />
                  </TableHeaderRow>
                }
              >
                {isTableLoadingVisible() &&
                  skeletonArray.map((_, index) => (
                    <TableRow id={`row-skeleton-${index}`} key={`skeletonTableLeftColumnRow-${_.id}`}>
                      <TableCell isLoading firstLineText="" flex="0 0 100%" />
                    </TableRow>
                  ))}
                {isTableGroupsRowsWithDataVisible() &&
                  getFilteredGroups()?.map((group, id) => (
                    <TableRow
                      onClick={() => setSelectedGroupId(group.id)}
                      id={`row-group-${id}`}
                      hoverEffect
                      hoverAfterIconEffect
                      selected={group.id === selectedGroupId}
                      key={group.id}
                      beforeContentIconProps={{
                        name: 'CoContractor',
                        width: 24,
                        height: 24,
                        viewBox: '0 0 32 32',
                        color: 'lTextHigh',
                        stroke: 'transparent'
                      }}
                      beforeContentText={
                        group.maxNumberOfMembers
                          ? getBeforeContentText(group.members.length, group.maxNumberOfMembers)
                          : undefined
                      }
                      beforeContentTextColor={
                        group.maxNumberOfMembers
                          ? getBeforeContentTextColor(group.members.length, group.maxNumberOfMembers)
                          : undefined
                      }
                      afterContentIconProps={{
                        name: 'EditPen',
                        id: `row-group-${id}-edit`,
                        width: 24,
                        height: 24,
                        onClick: () =>
                          handleEditExternalUserGroupClick(group.name, group.id, group.maxNumberOfMembers || null)
                      }}
                    >
                      <TableCell
                        firstLineText={group.name}
                        firstLineId={`group-row-${id}-name`}
                        firstLineColor={group.id === selectedGroupId ? 'primary' : 'lTextHigh'}
                        isRowWithIconAndSwitch
                        flex="0 0 100%"
                      />
                    </TableRow>
                  ))}
              </Table>
            </ComponentWrapper>
            <ComponentWrapper height="calc(100vh - 23rem)" width="10.5rem" flex="0 0 70%">
              <Table
                header={
                  <TableHeaderRow placeForAvatarOrSwitchOrCheckbox>
                    <TableHeader
                      headerText={columnsUsers[0].text}
                      id={kebabCase(`header-${columnsUsers[0].text}`)}
                      flex={`0 0 ${columnsUsers[0].width}%`}
                      iconSorting={
                        isMemberSortingAvailable() ? handleSortingUsersIcon(columnsUsers[0].text) : undefined
                      }
                      onClick={
                        isMemberSortingAvailable() ? () => handleSortingUsersOnClick(columnsUsers[0].text) : undefined
                      }
                    />
                    <TableHeader
                      headerText={columnsUsers[1].text}
                      id={kebabCase(`header-${columnsUsers[1].text}`)}
                      flex={`0 0 ${columnsUsers[1].width}%`}
                      iconSorting={
                        isMemberSortingAvailable() ? handleSortingUsersIcon(columnsUsers[1].text) : undefined
                      }
                      onClick={
                        isMemberSortingAvailable() ? () => handleSortingUsersOnClick(columnsUsers[1].text) : undefined
                      }
                    />

                    <TableHeader
                      headerText={columnsUsers[2].text}
                      id={kebabCase(`header-${columnsUsers[2].text}`)}
                      flex={`0 0 ${columnsUsers[2].width}%`}
                      iconSorting={
                        isMemberSortingAvailable() ? handleSortingUsersIcon(columnsUsers[2].text) : undefined
                      }
                      onClick={
                        isMemberSortingAvailable() ? () => handleSortingUsersOnClick(columnsUsers[2].text) : undefined
                      }
                    />
                  </TableHeaderRow>
                }
              >
                {isTableLoadingVisible() &&
                  skeletonArray.map((_, index) => (
                    <TableRow id={`row-skeleton-${index}`} key={`skeletonTableRightColumnRow-${_.id}`}>
                      <TableCell isLoading firstLineText="" flex={`0 0 ${columnsUsers[0].width}%`} />
                      <TableCell isLoading firstLineText="" flex={`0 0 ${columnsUsers[1].width}%`} />
                      <TableCell isLoading firstLineText="" flex={`0 0 ${columnsUsers[2].width}%`} />
                    </TableRow>
                  ))}
                {isTableMembersRowsWithDataVisible() &&
                  getSelectedGroup()?.members.map((member, id) => (
                    <TableRow
                      id={`row-${id}`}
                      hoverEffect
                      onClick={() =>
                        handleEditExternalUserClick(
                          member.name,
                          member.email || null,
                          member.mobileDevices,
                          member.id,
                          selectedGroupId!,
                          member.isEditable!,
                          member.expirationDate || null,
                          getMemberRoleInSelectedGroup(member.id) || null,
                          member.isAdmin
                        )
                      }
                      beforeContentIconProps={
                        isMobileDeviceRegisteredWithAccess(member.mobileDevices)
                          ? { color: 'success', name: 'StatusConnected', viewBox: '0 0 14 14' }
                          : { color: 'error', name: 'StatusDisconnected', viewBox: '0 0 14 14' }
                      }
                      beforeContentIconTooltip={
                        isMobileDeviceRegisteredWithAccess(member.mobileDevices) ? (
                          <ComponentWrapper padding="8px">
                            <Typography color="textPrimary">{translation.app_connected}</Typography>
                          </ComponentWrapper>
                        ) : (
                          <ComponentWrapper padding="8px">
                            <Typography color="textPrimary">{translation.app_disconnected}</Typography>
                          </ComponentWrapper>
                        )
                      }
                      beforeContentIconTooltipPosition={
                        getSelectedGroup()?.members.length === id + 1
                          ? { left: '0', bottom: '0' }
                          : { top: '0', left: '0' }
                      }
                      key={`TableRowMember-${member.id}`}
                    >
                      <TableCell
                        firstLineText={member.name}
                        firstLineId={`row-${id}-name`}
                        secondLineText={getSecondLineText(member.email)}
                        secondLineId={`row-${id}-email`}
                        flex={`0 0 ${columnsUsers[0].width}%`}
                      />
                      <TableCell
                        firstLineText={getMemberRoleCellText(member.id) || '-'}
                        firstLineId={`row-${id}-role`}
                        flex={`0 0 ${columnsUsers[1].width}%`}
                      />
                      <TableCell
                        firstLineText={
                          member.expirationDate
                            ? dayjs(member.expirationDate).format('DD-MM-YYYY HH:mm')
                            : `${translation.never_expire}`
                        }
                        firstLineColor={handleColorDate(member.expirationDate || null)}
                        firstLineId={`row-${id}-expiration-date`}
                        flex={`0 0 ${columnsUsers[2].width}%`}
                      />
                    </TableRow>
                  ))}
                {isTableMembersNoMembersVisible() && (
                  <ComponentWrapper justifyContent="center" alignItems="center" height="calc(100vh - 23rem)">
                    <NoExternalUsersWithButton
                      disableButton={isButtonAddMemberDisabled()}
                      handleOnButtonClick={() => handleAddExternalUserClick(getSelectedGroup()!)}
                    />
                  </ComponentWrapper>
                )}
              </Table>
            </ComponentWrapper>
          </>
        )}
        {isTableNoGroupsVisible() && <NoExternalUserGroups handleOnButtonClick={handleAddExternalUserGroupClick} />}
      </ComponentWrapper>
      <Drawer />
      <Modal />
    </PageContent>
  );
};
export default ExternalUsers;
